import type { components } from "@majitap/sdk";
import { queryOptions } from "@tanstack/react-query";
import { api, ok } from "./api";
import type { Tone } from "./labels";

type Schemas = components["schemas"];
export type Rule = Schemas["PricingRule"];
export type RuleState = Rule["state"];
export type Basis = Rule["basis"];
export type ChargeType = Schemas["ChargeType"];
export type Quote = Schemas["StaffQuote"];
export type QuoteLine = Quote["deliveries"][number]["lines"][number];

export const RULE_STATE: Record<RuleState, [label: string, tone: Tone]> = {
  scheduled: ["Scheduled", "info"],
  active: ["In force", "success"],
  ended: ["Ended", "neutral"],
};

export const BASIS: Record<Basis, string> = {
  per_unit: "Per unit",
  per_delivery: "Per delivery",
  percent: "Percent",
};

export const CATEGORY: Record<ChargeType["category"], string> = {
  price: "Price",
  surcharge: "Surcharge",
  discount: "Discount",
  cost: "Cost",
  tax: "Tax",
};

export const PAYEE: Record<ChargeType["payee"], string> = {
  company: "The company",
  truck_partner: "The truck's partner",
  fillsite_partner: "The fill site's partner",
  third_party: "Someone else",
};

/** The scope columns, most specific first (the order a rule wins in, SCHEMA.md → Pricing). */
export const SCOPE_KEYS = [
  "location_id",
  "customer_id",
  "business_id",
  "fillsite_id",
  "partner_id",
  "geofence_id",
  "product_item_id",
  "category_id",
] as const;
type ScopeKey = (typeof SCOPE_KEYS)[number];
const NAME_OF: Record<ScopeKey, keyof Rule["scope_names"]> = {
  location_id: "location",
  customer_id: "customer",
  business_id: "business",
  fillsite_id: "fillsite",
  partner_id: "partner",
  geofence_id: "geofence",
  product_item_id: "product_item",
  category_id: "category",
};
const SCOPE_LABEL: Record<ScopeKey, string> = {
  location_id: "Place",
  customer_id: "Customer",
  business_id: "Business",
  fillsite_id: "Fill site",
  partner_id: "Partner",
  geofence_id: "Zone",
  product_item_id: "Item",
  category_id: "Category",
};

/** "Water · 10,000 L in Kilimani for Savannah Ltd", or "Everyone" for a rule with no scope. */
export function scopeText(rule: Pick<Rule, "scope" | "scope_names">): string {
  const parts = SCOPE_KEYS.filter((k) => rule.scope[k]).map(
    (k) => `${SCOPE_LABEL[k]}: ${rule.scope_names[NAME_OF[k]] ?? "unknown"}`,
  );
  return parts.length ? parts.join(" · ") : "Everyone, everywhere";
}

/**
 * A rule's history: every rule with the same charge type and exactly the same scope (a supersede
 * keeps both), oldest first. Rules with a wider or narrower scope are other prices, not versions.
 */
export function historyOf(rule: Pick<Rule, "charge_type" | "scope">, rules: Rule[]): Rule[] {
  return rules
    .filter((r) => r.charge_type.id === rule.charge_type.id && SCOPE_KEYS.every((k) => r.scope[k] === rule.scope[k]))
    .sort((a, b) => Date.parse(a.starts_at) - Date.parse(b.starts_at));
}

/** "6,500.00 per unit", "16% ", depending on the basis. The amount is the API's string. */
export function amountText(rule: Pick<Rule, "basis" | "amount">, money: (v: string) => string): string {
  if (rule.basis === "percent") return `${rule.amount}%`;
  return `${money(rule.amount)} ${rule.basis === "per_unit" ? "per unit" : "per delivery"}`;
}

/** What a rule's page offers: supersede or end while it's scheduled or in force; delete only before it takes effect. */
export function ruleActionsFor(state: RuleState) {
  return { supersede: state !== "ended", end: state !== "ended", remove: state === "scheduled" };
}

export const ruleQuery = (id: string) =>
  queryOptions({
    queryKey: ["pricing", "rule", id],
    queryFn: () => ok(api.GET("/v1/admin/pricing/{id}", { params: { path: { id } } })),
  });

export const chargeTypesQuery = queryOptions({
  queryKey: ["pricing", "charge-types"],
  staleTime: 60_000,
  queryFn: async () => (await ok(api.GET("/v1/admin/pricing/charge-types"))).data,
});

export const zonesQuery = queryOptions({
  queryKey: ["geofences", "names"],
  staleTime: 5 * 60_000,
  queryFn: async () => (await ok(api.GET("/v1/admin/geofences"))).data,
});

const path = (id: string) => ({ params: { path: { id } } });

export const pricingActions = {
  create: (body: Schemas["CreatePricingBody"]) => ok(api.POST("/v1/admin/pricing", { body })),
  supersede: (id: string, body: Schemas["SupersedeBody"]) =>
    ok(api.POST("/v1/admin/pricing/{id}/supersede", { ...path(id), body })),
  end: (id: string, body: Schemas["EndPricingBody"]) =>
    ok(api.POST("/v1/admin/pricing/{id}/end", { ...path(id), body })),
  remove: (id: string) => ok(api.DELETE("/v1/admin/pricing/{id}", path(id))),
  createType: (body: Schemas["CreateChargeTypeBody"]) => ok(api.POST("/v1/admin/pricing/charge-types", { body })),
  updateType: (id: string, body: Schemas["UpdateChargeTypeBody"]) =>
    ok(api.PATCH("/v1/admin/pricing/charge-types/{id}", { ...path(id), body })),
  removeType: (id: string) => ok(api.DELETE("/v1/admin/pricing/charge-types/{id}", path(id))),
  quote: (body: Schemas["StaffQuoteBody"]) => ok(api.POST("/v1/admin/pricing/quote", { body })),
};
